// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — lib/consultas.ts
//
//  Tipos do contrato de listagem/seleção (PKG-00). Espelham o backend:
//    PaginadoResponse<T>  → API/Udisys.Api/DTOs/Common/PaginacaoDto.cs
//    LookupItem           → API/Udisys.Api/DTOs/Common/LookupDto.cs
//  Guia: docs/arquitetura/PADRAO_PAGINAS_CADASTRO.md, "Consultas e seleção de entidades".
// ════════════════════════════════════════════════════════════════════════════

import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from "date-fns";
import { apiClient } from "@/lib/apiClient";

export type DirecaoOrdenacao = "asc" | "desc";

export interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

/** Item do endpoint `GET .../lookup?busca=&limite=` (convenção de seleção). */
export interface LookupItem {
  id: number;
  codigo: string | null;
  descricao: string;
  extra?: string | null;
}

/** Chama um endpoint de lookup da convenção. */
export async function buscarLookup(url: string, busca: string, limite = 20): Promise<LookupItem[]> {
  const { data } = await apiClient.get<LookupItem[]>(url, { params: { busca: busca.trim() || undefined, limite } });
  return data;
}

/** Rótulo padrão "Código — Nome" (sem código, só o nome). */
export function rotuloCodigoNome(codigo: string | number | null | undefined, nome: string): string {
  return codigo === null || codigo === undefined || codigo === "" ? nome : `${codigo} — ${nome}`;
}

/** Comparação de texto para ordenação local: pt-BR, sem diferenciar acento/maiúscula, números naturais. */
export const compararTexto = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true }).compare;

// ── Ordenação ──────────────────────────────────────────────────────────────

/**
 * Próximo estado ao clicar num cabeçalho: mesmo campo inverte; campo novo entra
 * em `direcaoPadrao` (use "desc" para datas e valores).
 */
export function alternarOrdenacao<T extends string>(
  atual: { ordenarPor: T | string; direcao: DirecaoOrdenacao },
  campo: T,
  direcaoPadrao: DirecaoOrdenacao = "asc",
): { ordenarPor: T; direcao: DirecaoOrdenacao } {
  if (atual.ordenarPor === campo) return { ordenarPor: campo, direcao: atual.direcao === "asc" ? "desc" : "asc" };
  return { ordenarPor: campo, direcao: direcaoPadrao };
}

// ── Período (FiltroPeriodo) ────────────────────────────────────────────────

/** Datas "yyyy-MM-dd" (dia local); vazio = sem limite. */
export interface Periodo {
  inicio: string;
  fim: string;
}

export type AtalhoPeriodo = "todos" | "hoje" | "semana" | "mes";

const iso = (d: Date) => format(d, "yyyy-MM-dd");

/** Intervalo de um atalho na data de referência (semana começa na segunda). */
export function periodoDoAtalho(atalho: AtalhoPeriodo, hoje = new Date()): Periodo {
  switch (atalho) {
    case "hoje":   return { inicio: iso(hoje), fim: iso(hoje) };
    case "semana": return { inicio: iso(startOfWeek(hoje, { weekStartsOn: 1 })), fim: iso(endOfWeek(hoje, { weekStartsOn: 1 })) };
    case "mes":    return { inicio: iso(startOfMonth(hoje)), fim: iso(endOfMonth(hoje)) };
    default:       return { inicio: "", fim: "" };
  }
}
